import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Wallet, LogOut, Search, Check, X, Users, Clock, ShieldCheck, Ban, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
  demo: "bg-slate-100 text-slate-600",
};

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [tab, setTab] = useState("pending");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users);
      setStats(data.stats);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (uid, status) => {
    setBusy(uid + status);
    try {
      await api.post(`/admin/users/${uid}/status`, { status });
      toast.success(`User ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const removeUser = async (uid) => {
    setBusy(uid + "delete");
    try {
      await api.delete(`/admin/users/${uid}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const filtered = users.filter((u) => {
    if (u.role === "admin") return false;
    const matchTab = tab === "all" ? true : u.status === tab;
    const matchQ = !q || u.name.toLowerCase().includes(q.toLowerCase()) || u.email.toLowerCase().includes(q.toLowerCase());
    return matchTab && matchQ;
  });

  const StatCard = ({ icon: Icon, label, value, color, testid }) => (
    <div data-testid={testid} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}1a`, color }}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="font-mono-nums text-xl font-bold text-slate-900">{value}</p>
        <p className="text-xs text-slate-400">{label}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-orange-600 flex items-center justify-center text-white"><Wallet className="w-5 h-5" /></div>
          <div>
            <span className="font-display text-lg font-bold">ePitaka</span>
            <Badge className="ml-2 bg-slate-900 text-white hover:bg-slate-900">Admin</Badge>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500 hidden sm:block">{user?.email}</span>
          <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout-button"><LogOut className="w-4 h-4 mr-1.5" /> Sign out</Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">User Management</p>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Approval Center</h1>
          </div>
          <Button variant="ghost" size="sm" onClick={load} data-testid="admin-refresh"><RefreshCw className="w-4 h-4 mr-1.5" /> Refresh</Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
          <StatCard icon={Users} label="Total users" value={stats.total} color="#0F172A" testid="admin-stat-total" />
          <StatCard icon={Clock} label="Pending" value={stats.pending} color="#D97706" testid="admin-stat-pending" />
          <StatCard icon={ShieldCheck} label="Approved" value={stats.approved} color="#059669" testid="admin-stat-approved" />
          <StatCard icon={Ban} label="Rejected" value={stats.rejected} color="#DC2626" testid="admin-stat-rejected" />
        </div>

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div className="flex gap-1 p-1 bg-slate-100 rounded-xl text-sm font-medium">
            {TABS.map((t) => (
              <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)}
                className={`px-3 py-1.5 rounded-lg transition-colors ${tab === t.key ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="relative sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="admin-search" className="pl-9" placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>

        <div className="mt-4 bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {filtered.length === 0 && <p className="text-center text-sm text-slate-400 py-12">No users in this view.</p>}
          {filtered.map((u, i) => (
            <motion.div key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              className="flex items-center gap-3 px-4 py-3 border-b border-slate-50 last:border-0">
              <Avatar className="w-9 h-9 border border-slate-200"><AvatarFallback className="bg-orange-100 text-orange-700 text-xs font-semibold">{(u.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{u.name || "Unnamed"}</p>
                <p className="text-xs text-slate-400 truncate">{u.email} · {u.auth_provider}</p>
              </div>
              <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_STYLE[u.status]}`}>{u.status}</span>
              <div className="flex items-center gap-1.5">
                {u.status !== "approved" && (
                  <Button size="sm" disabled={busy === u.user_id + "approved"} onClick={() => setStatus(u.user_id, "approved")}
                    className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`admin-approve-${u.user_id}`}>
                    <Check className="w-3.5 h-3.5 mr-1" /> Approve
                  </Button>
                )}
                {u.status !== "rejected" && (
                  <Button size="sm" variant="outline" disabled={busy === u.user_id + "rejected"} onClick={() => setStatus(u.user_id, "rejected")}
                    className="h-8 text-red-600 border-red-200 hover:bg-red-50" data-testid={`admin-reject-${u.user_id}`}>
                    <X className="w-3.5 h-3.5 mr-1" /> Reject
                  </Button>
                )}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="icon" variant="ghost" disabled={busy === u.user_id + "delete"}
                      className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`admin-delete-${u.user_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {u.name || u.email}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This permanently removes the account and all of its bills, savings, income and other records. This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid={`admin-delete-cancel-${u.user_id}`}>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => removeUser(u.user_id)} className="bg-red-600 hover:bg-red-700" data-testid={`admin-delete-confirm-${u.user_id}`}>
                        Delete user
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
}
